import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Snowflake, Truck, FlameKindling, Sparkles, Bug, Plus, X, AlertTriangle, Loader2, Settings } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { companiesApi } from '../lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('safetydesk81_token')}` });

const TABS = [
  { key: 'frigo', label: 'Frigoriferi e congelatori', icon: Snowflake },
  { key: 'fornitori', label: 'Fornitori qualificati', icon: Truck },
  { key: 'abbattimento', label: 'Abbattimento', icon: FlameKindling },
  { key: 'pulizia', label: 'Pulizia e sanificazione', icon: Sparkles },
  { key: 'insetti', label: 'Insetti e roditori', icon: Bug },
];

const RegistriHaccpPage = ({ user }) => {
  const isClient = user?.role === 'client';
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [cucine, setCucine] = useState([]);
  const [selectedCucinaId, setSelectedCucinaId] = useState(null);
  const [tab, setTab] = useState('frigo');
  const [catalog, setCatalog] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load companies + catalog on mount
  useEffect(() => {
    companiesApi.list({ status: 'all' }).then((r) => {
      const list = r.data || [];
      setCompanies(list);
      // Auto-select for client with single company
      if (isClient && list.length === 1) setSelectedCompanyId(list[0].id);
    });
    axios.get(`${API}/haccp-registri/catalog`, { headers: authHeaders() }).then((r) => setCatalog(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load cucine when companyId changes
  useEffect(() => {
    if (!selectedCompanyId) { setCucine([]); setSelectedCucinaId(null); return; }
    setLoading(true);
    axios.get(`${API}/haccp-registri/cucine`, { params: { companyId: selectedCompanyId }, headers: authHeaders() })
      .then((r) => {
        setCucine(r.data || []);
        if ((r.data || []).length === 1) setSelectedCucinaId(r.data[0].id);
        else if ((r.data || []).length > 1 && !selectedCucinaId) setSelectedCucinaId(r.data[0].id);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  const selectedCucina = cucine.find((c) => c.id === selectedCucinaId);
  const selectedCompany = companies.find((c) => c.id === selectedCompanyId);

  return (
    <div className="space-y-5" data-testid="registri-haccp-page">
      {/* HEADER */}
      <div className="rounded-2xl bg-gradient-to-r from-[#023F80] to-[#1e40af] p-5 text-white shadow-lg">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold">Registri HACCP</h2>
            <p className="text-sm text-blue-100 mt-0.5">
              {isClient ? 'Compila i registri della tua cucina.' : 'Configura cucine, dotazioni e aree. Consulta le compilazioni.'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!isClient && (
              <select value={selectedCompanyId || ''} onChange={(e) => { setSelectedCompanyId(e.target.value); setSelectedCucinaId(null); }} data-testid="rh-company-select" className="px-3 py-2 rounded-lg bg-white/10 border border-white/30 text-white text-sm font-semibold">
                <option value="" className="text-slate-800">Seleziona azienda…</option>
                {companies.filter((c) => c.id && c.id !== 'privati').map((c) => <option key={c.id} value={c.id} className="text-slate-800">{c.name}</option>)}
              </select>
            )}
            {cucine.length > 1 && (
              <select value={selectedCucinaId || ''} onChange={(e) => setSelectedCucinaId(e.target.value)} data-testid="rh-cucina-select" className="px-3 py-2 rounded-lg bg-white/10 border border-white/30 text-white text-sm font-semibold">
                {cucine.map((c) => <option key={c.id} value={c.id} className="text-slate-800">{c.name}</option>)}
              </select>
            )}
            {!isClient && selectedCompanyId && (
              <button onClick={() => setConfigOpen(true)} data-testid="rh-config-btn" className="ro-hide inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#FFCB05] text-[#023F80] text-xs font-bold hover:bg-yellow-400">
                <Settings size={14} /> Configura cucine
              </button>
            )}
          </div>
        </div>
      </div>

      {/* BREADCRUMB */}
      {selectedCompany && selectedCucina && (
        <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-2 text-xs text-slate-600 flex flex-wrap items-center gap-2" data-testid="rh-breadcrumb">
          <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Azienda</span>
          <span className="font-semibold text-slate-800">{selectedCompany.name}</span>
          <span className="text-slate-400">·</span>
          <span className="font-bold text-slate-500 uppercase tracking-wider text-[10px]">Cucina</span>
          <span className="font-semibold text-[#023F80]">{selectedCucina.name}</span>
          {selectedCucina.sede && <>
            <span className="text-slate-400">·</span>
            <span className="text-slate-600">{selectedCucina.sede}</span>
          </>}
        </div>
      )}

      {/* GUIDE EMPTY STATE */}
      {!selectedCompanyId && !isClient && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800" data-testid="rh-pick-company-hint">
          Seleziona un'azienda per visualizzare le sue cucine e configurarle.
        </div>
      )}
      {selectedCompanyId && !loading && cucine.length === 0 && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
          {isClient ? 'Nessuna cucina ancora configurata per la tua azienda. Contatta il consulente.' : 'Nessuna cucina configurata. Clicca "Configura cucine" per crearne una.'}
        </div>
      )}

      {/* TABS */}
      {selectedCucina && (
        <>
          <div className="flex gap-1 flex-wrap" data-testid="rh-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button key={t.key} onClick={() => setTab(t.key)} data-testid={`rh-tab-${t.key}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border-2 transition ${active ? 'bg-[#023F80] text-white border-[#023F80]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}>
                  <Icon size={14} />
                  {t.label}
                </button>
              );
            })}
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 min-h-[300px]" data-testid={`rh-panel-${tab}`}>
            {tab === 'frigo' && <FrigoPanel cucinaId={selectedCucinaId} isClient={isClient} />}
            {tab === 'abbattimento' && <AbbattimentoPanel cucinaId={selectedCucinaId} isClient={isClient} />}
            {tab === 'pulizia' && <PuliziaPanel cucinaId={selectedCucinaId} isClient={isClient} catalog={catalog} />}
            {tab === 'fornitori' && <FornitoriPanel cucinaId={selectedCucinaId} isClient={isClient} />}
            {tab === 'insetti' && <InsettiPanel cucinaId={selectedCucinaId} catalog={catalog} isClient={isClient} />}
          </div>
        </>
      )}

      {configOpen && selectedCompanyId && (
        <ConfigDialog
          companyId={selectedCompanyId}
          catalog={catalog}
          cucine={cucine}
          onClose={() => setConfigOpen(false)}
          onChanged={() => {
            axios.get(`${API}/haccp-registri/cucine`, { params: { companyId: selectedCompanyId }, headers: authHeaders() })
              .then((r) => setCucine(r.data || []));
          }}
        />
      )}
    </div>
  );
};

// ─────────────── PULIZIE (A/B/C/D/E) ───────────────
const STATO_CYCLE = { da_compilare: 'eseguito', eseguito: 'non_eseguito', non_eseguito: 'da_compilare' };
const STATO_SHORT = { da_compilare: '', eseguito: '✓', non_eseguito: '✗' };
const STATO_CLS = {
  da_compilare: 'bg-white text-slate-400 hover:bg-slate-50',
  eseguito: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200',
  non_eseguito: 'bg-red-100 text-red-800 hover:bg-red-200',
};
const ESITO_CYCLE = { da_compilare: 'C', C: 'NC', NC: 'da_compilare' };
const ESITO_CLS = {
  da_compilare: 'bg-slate-50 text-slate-400',
  C: 'bg-emerald-100 text-emerald-800',
  NC: 'bg-red-200 text-red-900 font-bold',
};

const PuliziaPanel = ({ cucinaId, catalog, isClient }) => {
  const [config, setConfig] = useState([]);
  const [scheda, setScheda] = useState(null);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  useEffect(() => {
    axios.get(`${API}/haccp-registri/cucine/${cucinaId}/pulizie/config`, { headers: authHeaders() })
      .then((r) => {
        const enabled = (r.data || []).filter((c) => c.enabled);
        setConfig(enabled);
        if (enabled.length && !scheda) setScheda(enabled[0].scheda);
      });
    // eslint-disable-next-line
  }, [cucinaId]);

  if (config.length === 0) {
    return (
      <div className="text-sm text-slate-500 italic text-center py-6" data-testid="pul-empty">
        Nessuna scheda di pulizia è stata attivata per questa cucina. {!isClient && 'Vai in "Configura cucine → Schede pulizia" per abilitarle.'}
      </div>
    );
  }

  const schedaCfg = config.find((c) => c.scheda === scheda);
  return (
    <div className="space-y-3" data-testid="pul-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-lg font-bold text-[#023F80]">Pulizia e sanificazione</h3>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="flex gap-1">
            {config.map((c) => (
              <button key={c.scheda} onClick={() => setScheda(c.scheda)} data-testid={`pul-tab-${c.scheda}`}
                className={`px-2.5 py-1 rounded text-xs font-bold border-2 ${scheda === c.scheda ? 'bg-[#023F80] text-white border-[#023F80]' : 'bg-white text-slate-600 border-slate-200'}`}
                title={c.label}>
                {c.scheda}
              </button>
            ))}
          </div>
          {schedaCfg?.frequency !== 'monthly' && (
            <select value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))} className="px-2 py-1 border border-slate-300 rounded text-sm">
              {MESI.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          )}
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} className="px-2 py-1 border border-slate-300 rounded text-sm">
            {[2024, 2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {schedaCfg && (
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900">
          <b>Scheda {schedaCfg.scheda}:</b> {schedaCfg.label} · frequenza <b>{schedaCfg.frequency}</b>
          {schedaCfg.needs_outcome && <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 text-[10px] font-bold">RICHIEDE ESITO C/NC</span>}
        </div>
      )}

      {schedaCfg && schedaCfg.frequency === 'daily' && <SchedaDaily cucinaId={cucinaId} cfg={schedaCfg} year={year} month={month} />}
      {schedaCfg && schedaCfg.frequency === 'weekly' && <SchedaWeekly cucinaId={cucinaId} cfg={schedaCfg} year={year} month={month} />}
      {schedaCfg && schedaCfg.frequency === 'monthly' && <SchedaMonthly cucinaId={cucinaId} cfg={schedaCfg} year={year} />}

      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-700 italic">
        Legenda stati esecuzione: <b>vuoto</b> = Da compilare · <b>✓</b> = Eseguito · <b>✗</b> = Non eseguito. Nessuna cella vuota è interpretata come attività svolta. La sigla NON costituisce firma elettronica qualificata.
      </div>
    </div>
  );
};

// Hook comune per load+upsert pulizie
const usePulizieRows = (cucinaId, scheda, year, month) => {
  const [rows, setRows] = useState({});
  const reload = () => {
    const params = { scheda, year };
    if (month !== null && month !== undefined) params.month = month;
    axios.get(`${API}/haccp-registri/cucine/${cucinaId}/pulizie`, { params, headers: authHeaders() })
      .then((r) => {
        const m = {};
        (r.data?.rows || []).forEach((row) => {
          const key = `${row.month || 0}-${row.day || 0}-${row.itemIndex}`;
          m[key] = row;
        });
        setRows(m);
      });
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [cucinaId, scheda, year, month]);
  const save = async (patch) => {
    try {
      const { data } = await axios.put(`${API}/haccp-registri/pulizie`, { cucinaId, scheda, year, month, ...patch }, { headers: authHeaders() });
      const key = `${data.month || 0}-${data.day || 0}-${data.itemIndex}`;
      setRows((r) => ({ ...r, [key]: data }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  return { rows, save };
};

const Cell = ({ stato, onToggle, locked }) => (
  <button onClick={locked ? undefined : onToggle} className={`w-full h-7 border border-slate-200 rounded text-xs font-bold ${STATO_CLS[stato || 'da_compilare']}`} data-testid="pul-cell">
    {STATO_SHORT[stato || 'da_compilare']}
  </button>
);
const EsitoCell = ({ esito, onToggle }) => (
  <button onClick={onToggle} className={`w-full h-6 border border-slate-200 rounded text-[10px] ${ESITO_CLS[esito || 'da_compilare']}`}>
    {esito === 'da_compilare' || !esito ? '—' : esito}
  </button>
);
const SigInput = ({ value, onBlur }) => (
  <input type="text" defaultValue={value || ''} onBlur={(e) => { if ((value || '') !== e.target.value) onBlur(e.target.value); }}
    className="w-full px-1 py-0.5 border border-slate-200 rounded text-[10px]" placeholder="sigla" />
);

const SchedaDaily = ({ cucinaId, cfg, year, month }) => {
  const { rows, save } = usePulizieRows(cucinaId, cfg.scheda, year, month);
  const days = daysInMonth(year, month);
  const nextStato = (s) => STATO_CYCLE[s || 'da_compilare'];
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs border border-slate-200">
        <thead className="bg-slate-100">
          <tr>
            <th className="px-2 py-1 text-left sticky left-0 bg-slate-100 z-10">Voce</th>
            {Array.from({ length: days }, (_, i) => i + 1).map((d) => (
              <th key={d} className="px-1 py-1 w-10">{d}</th>
            ))}
            {cfg.needs_outcome && <th className="px-2 py-1 bg-amber-100">Esito</th>}
          </tr>
        </thead>
        <tbody>
          {cfg.items.map((item, idx) => (
            <React.Fragment key={idx}>
              <tr className="border-t border-slate-200">
                <td className="px-2 py-1 font-semibold sticky left-0 bg-white z-10" rowSpan={2}>{item}</td>
                {Array.from({ length: days }, (_, i) => i + 1).map((d) => {
                  const row = rows[`${month}-${d}-${idx}`];
                  return (
                    <td key={d} className="p-0.5">
                      <Cell stato={row?.stato} onToggle={() => save({ day: d, itemIndex: idx, stato: nextStato(row?.stato), esito: row?.esito, firma: row?.firma })} />
                    </td>
                  );
                })}
                {cfg.needs_outcome && <td className="p-0.5 bg-amber-50" rowSpan={2}>
                  {/* esito aggregato: C se tutti giorni compilati conformi, NC se almeno uno NC; qui offriamo cella manuale sul primo giorno come esempio */}
                  <span className="text-[9px] text-slate-500">per giorno</span>
                </td>}
              </tr>
              <tr>
                {Array.from({ length: days }, (_, i) => i + 1).map((d) => {
                  const row = rows[`${month}-${d}-${idx}`];
                  return (
                    <td key={d} className="p-0.5">
                      {cfg.needs_outcome ? (
                        <EsitoCell esito={row?.esito} onToggle={() => save({ day: d, itemIndex: idx, stato: row?.stato || 'da_compilare', esito: ESITO_CYCLE[row?.esito || 'da_compilare'], firma: row?.firma })} />
                      ) : (
                        <SigInput value={row?.firma} onBlur={(v) => save({ day: d, itemIndex: idx, stato: row?.stato || 'da_compilare', esito: row?.esito, firma: v })} />
                      )}
                    </td>
                  );
                })}
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SchedaWeekly = ({ cucinaId, cfg, year, month }) => {
  const { rows, save } = usePulizieRows(cucinaId, cfg.scheda, year, month);
  // 5 settimane possibili nel mese: giorni rappresentativi 1,8,15,22,29
  const giorni = [1, 8, 15, 22, 29];
  const nextStato = (s) => STATO_CYCLE[s || 'da_compilare'];
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs border border-slate-200">
        <thead className="bg-slate-100">
          <tr>
            <th className="px-2 py-1 text-left">Voce</th>
            {giorni.map((g) => <th key={g} className="px-2 py-1">Sett. del {g}/{month}</th>)}
          </tr>
        </thead>
        <tbody>
          {cfg.items.map((item, idx) => (
            <tr key={idx} className="border-t border-slate-200">
              <td className="px-2 py-1 font-semibold">{item}</td>
              {giorni.map((g) => {
                const row = rows[`${month}-${g}-${idx}`];
                return (
                  <td key={g} className="p-1 min-w-[120px]">
                    <Cell stato={row?.stato} onToggle={() => save({ day: g, itemIndex: idx, stato: nextStato(row?.stato), firma: row?.firma })} />
                    <SigInput value={row?.firma} onBlur={(v) => save({ day: g, itemIndex: idx, stato: row?.stato || 'da_compilare', firma: v })} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const SchedaMonthly = ({ cucinaId, cfg, year }) => {
  const { rows, save } = usePulizieRows(cucinaId, cfg.scheda, year, null);
  const nextStato = (s) => STATO_CYCLE[s || 'da_compilare'];
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs border border-slate-200">
        <thead className="bg-slate-100">
          <tr>
            <th className="px-2 py-1 text-left sticky left-0 bg-slate-100">Voce / Mese</th>
            {MESI.map((m, i) => <th key={m} className="px-1 py-1">{m.slice(0, 3)}</th>)}
          </tr>
        </thead>
        <tbody>
          {cfg.items.map((item, idx) => (
            <React.Fragment key={idx}>
              <tr className="border-t border-slate-200">
                <td className="px-2 py-1 font-semibold sticky left-0 bg-white" rowSpan={2}>{item}</td>
                {MESI.map((_, mi) => {
                  const row = rows[`${mi + 1}-0-${idx}`];
                  return (
                    <td key={mi} className="p-0.5">
                      <Cell stato={row?.stato} onToggle={() => save({ month: mi + 1, day: null, itemIndex: idx, stato: nextStato(row?.stato), firma: row?.firma })} />
                    </td>
                  );
                })}
              </tr>
              <tr>
                {MESI.map((_, mi) => {
                  const row = rows[`${mi + 1}-0-${idx}`];
                  return (
                    <td key={mi} className="p-0.5">
                      <SigInput value={row?.firma} onBlur={(v) => save({ month: mi + 1, day: null, itemIndex: idx, stato: row?.stato || 'da_compilare', firma: v })} />
                    </td>
                  );
                })}
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─────────────── ABBATTIMENTO ───────────────
const AbbattimentoPanel = ({ cucinaId, isClient }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [throwPopup, setThrowPopup] = useState(null); // {prodotto, quantita}

  const reload = () => {
    setLoading(true);
    axios.get(`${API}/haccp-registri/cucine/${cucinaId}/abbattimenti`, { headers: authHeaders() })
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [cucinaId]);

  const del = async (id) => {
    if (!window.confirm('Eliminare questa registrazione? Lo storico sarà rimosso.')) return;
    try { await axios.delete(`${API}/haccp-registri/abbattimenti/${id}`, { headers: authHeaders() }); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const fmtDT = (iso) => iso ? iso.replace('T', ' ').slice(0, 16) : '';

  return (
    <div className="space-y-4" data-testid="abb-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-bold text-[#023F80]">Registro abbattimento rapido</h3>
          <p className="text-xs text-slate-500">Compila per ogni abbattimento. Rev. corrente · Data scheda: <b>{new Date().toISOString().slice(0, 10)}</b></p>
        </div>
        <button onClick={() => setFormOpen(true)} data-testid="abb-add-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#023F80] text-white text-xs font-bold hover:bg-[#022F60]">
          <Plus size={14} /> Nuovo abbattimento
        </button>
      </div>
      {loading && <Loader2 className="animate-spin text-slate-500" size={16} />}
      {!loading && items.length === 0 && <div className="text-sm text-slate-500 italic">Nessun abbattimento registrato.</div>}
      {!loading && items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs border border-slate-200">
            <thead className="bg-slate-100">
              <tr className="text-left">
                <th className="px-2 py-1.5">Data</th>
                <th className="px-2 py-1.5">Prodotto · quantità</th>
                <th className="px-2 py-1.5">Inizio</th>
                <th className="px-2 py-1.5">Fine</th>
                <th className="px-2 py-1.5">Durata</th>
                <th className="px-2 py-1.5">Sez. 7 rispettata?</th>
                <th className="px-2 py-1.5">Esito</th>
                <th className="px-2 py-1.5">Sigla</th>
                <th className="px-2 py-1.5 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className={`border-t border-slate-200 ${a.esito === 'NC' ? 'bg-red-50' : 'bg-white'}`} data-testid={`abb-row-${a.id}`}>
                  <td className="px-2 py-1 font-mono">{a.data}</td>
                  <td className="px-2 py-1">
                    <div className="font-semibold">{a.prodotto}</div>
                    {a.quantita && <div className="text-[10px] text-slate-500">{a.quantita}</div>}
                  </td>
                  <td className="px-2 py-1 font-mono">{fmtDT(a.inizio)}</td>
                  <td className="px-2 py-1 font-mono">{fmtDT(a.fine)}</td>
                  <td className="px-2 py-1 text-[10px]">{Math.floor(a.duration_min / 60)}h{String(a.duration_min % 60).padStart(2, '0')}'</td>
                  <td className="px-2 py-1">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${a.limitiRispettati ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                      {a.limitiRispettati ? 'SI' : 'NO'}
                    </span>
                  </td>
                  <td className="px-2 py-1">
                    {a.esito === 'NC' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold">
                        <AlertTriangle size={10} /> NC · BUTTARE
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold">C</span>
                    )}
                  </td>
                  <td className="px-2 py-1 font-mono">{a.firmaOperatore || '—'}</td>
                  <td className="px-2 py-1">
                    {!isClient && <button onClick={() => del(a.id)} className="px-1.5 py-0.5 text-[10px] bg-red-100 text-red-700 rounded hover:bg-red-200">Del</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-700 italic">
        La domanda "Sono stati rispettati i limiti previsti in sezione n° 7 per il raffreddamento rapido?" fa riferimento al Piano HACCP dell'azienda, sezione 7. L'app non calcola automaticamente conformità su tempi/temperature di abbattimento: la risposta è dichiarata dall'operatore.
      </div>
      {formOpen && <AbbattimentoForm cucinaId={cucinaId} onClose={() => setFormOpen(false)} onSaved={(doc) => {
        setFormOpen(false);
        reload();
        if (doc.prodottoDaButtare) setThrowPopup({ prodotto: doc.prodotto, quantita: doc.quantita });
      }} />}
      {throwPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="abb-throw-popup">
          <div className="absolute inset-0 bg-black/70" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 border-t-4 border-red-600 text-center">
            <AlertTriangle size={48} className="text-red-600 mx-auto mb-3" />
            <h3 className="text-2xl font-bold text-red-700">Buttare prodotto</h3>
            <p className="text-sm text-slate-700 mt-3">
              I limiti della <b>sezione 7</b> del piano HACCP non sono stati rispettati per:
            </p>
            <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
              <div className="font-bold text-slate-900">{throwPopup.prodotto}</div>
              {throwPopup.quantita && <div className="text-xs text-slate-600">{throwPopup.quantita}</div>}
            </div>
            <p className="text-xs text-slate-500 mt-3">
              La registrazione è stata salvata come <b className="text-red-700">NC</b>. Chiudere questa finestra non modifica l'esito.
            </p>
            <button onClick={() => setThrowPopup(null)} data-testid="abb-throw-close" className="mt-4 px-6 py-2 rounded-lg bg-red-600 text-white font-bold hover:bg-red-700">
              Ho capito
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const AbbattimentoForm = ({ cucinaId, onClose, onSaved }) => {
  const now = new Date();
  const nowLocal = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  const [prodotto, setProdotto] = useState('');
  const [quantita, setQuantita] = useState('');
  const [inizio, setInizio] = useState(nowLocal);
  const [fine, setFine] = useState('');
  const [risp, setRisp] = useState(null);  // null, true, false — NON pre-selezionato
  const [firma, setFirma] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const canSave = prodotto.trim() && inizio && fine && risp !== null;

  const submit = async () => {
    if (!canSave) return toast.error('Compila prodotto, inizio/fine e rispondi Sì/No alla sezione 7');
    if (new Date(fine) <= new Date(inizio)) return toast.error('La fine deve essere dopo l\'inizio (anche se oltre mezzanotte, basta avanzare la data).');
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/haccp-registri/abbattimenti`, {
        cucinaId, prodotto, quantita, inizio, fine, limitiRispettati: risp, firmaOperatore: firma, note,
      }, { headers: authHeaders() });
      toast.success(risp ? 'Abbattimento registrato come conforme' : 'Abbattimento registrato come NON conforme');
      onSaved(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="abb-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl p-5 space-y-3">
        <h3 className="text-lg font-bold text-[#023F80]">Nuovo abbattimento</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Field label="Prodotto *"><Input value={prodotto} onChange={(e) => setProdotto(e.target.value)} data-testid="abb-prodotto" placeholder="Es. Ragù di carne, lasagna…" /></Field>
          <Field label="Quantità"><Input value={quantita} onChange={(e) => setQuantita(e.target.value)} data-testid="abb-quantita" placeholder="Es. 5 kg, 3 teglie" /></Field>
          <Field label="Inizio (data e ora)"><Input type="datetime-local" value={inizio} onChange={(e) => setInizio(e.target.value)} data-testid="abb-inizio" /></Field>
          <Field label="Fine (data e ora — anche giorno successivo)"><Input type="datetime-local" value={fine} onChange={(e) => setFine(e.target.value)} data-testid="abb-fine" /></Field>
        </div>
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
          <div className="text-xs font-bold text-slate-700 mb-2">
            Sono stati rispettati i limiti previsti in <b>sezione n° 7</b> per il raffreddamento rapido?
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setRisp(true)} data-testid="abb-si" className={`px-4 py-1.5 rounded font-bold border-2 ${risp === true ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white text-slate-600 border-slate-300'}`}>SI</button>
            <button type="button" onClick={() => setRisp(false)} data-testid="abb-no" className={`px-4 py-1.5 rounded font-bold border-2 ${risp === false ? 'bg-red-600 text-white border-red-700' : 'bg-white text-slate-600 border-slate-300'}`}>NO</button>
          </div>
          {risp === false && (
            <div className="mt-2 text-[11px] text-red-800 font-semibold">
              <AlertTriangle className="inline mr-1" size={12} /> La registrazione sarà salvata come NC: alla conferma apparirà l'avviso "Buttare prodotto".
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Field label="Firma/Sigla operatore"><Input value={firma} onChange={(e) => setFirma(e.target.value)} data-testid="abb-firma" /></Field>
          <Field label="Note (opz.)"><Input value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        </div>
        <div className="text-[10px] text-slate-400 italic">La sigla NON costituisce firma elettronica qualificata.</div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 rounded-lg text-sm hover:bg-slate-100">Annulla</button>
          <button onClick={submit} disabled={!canSave || saving} data-testid="abb-save-btn" className="px-4 py-2 rounded-lg bg-[#023F80] text-white text-sm font-bold disabled:opacity-50">
            {saving ? 'Salvo…' : 'Salva abbattimento'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────── FRIGO ───────────────
const MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const daysInMonth = (y, m) => new Date(y, m, 0).getDate(); // m 1-12

const FrigoPanel = ({ cucinaId, isClient }) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [dotazioni, setDotazioni] = useState([]);
  const [dotId, setDotId] = useState(null);
  const [data, setData] = useState(null);  // { dotazione, days: {day: row} }
  const [loading, setLoading] = useState(false);
  const [savingDay, setSavingDay] = useState(null);

  useEffect(() => {
    axios.get(`${API}/haccp-registri/cucine/${cucinaId}/dotazioni`, { headers: authHeaders() })
      .then((r) => {
        setDotazioni(r.data || []);
        if ((r.data || []).length && !dotId) setDotId(r.data[0].id);
      });
    // eslint-disable-next-line
  }, [cucinaId]);

  const reload = () => {
    if (!dotId) return;
    setLoading(true);
    axios.get(`${API}/haccp-registri/cucine/${cucinaId}/frigo`, { params: { dotazioneId: dotId, year, month }, headers: authHeaders() })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [dotId, year, month]);

  const dotazione = data?.dotazione;
  const days = daysInMonth(year, month);

  const saveDay = async (day, patch) => {
    setSavingDay(day);
    try {
      const existing = data?.days?.[day] || {};
      const payload = {
        dotazioneId: dotId, year, month,
        t1: existing.t1, t2: existing.t2, t3: existing.t3,
        sigla: existing.sigla || '',
        igiene: existing.igiene || 'da_compilare',
        igiene_note: existing.igiene_note || '',
        ...patch,
      };
      const { data: row } = await axios.put(`${API}/haccp-registri/cucine/${cucinaId}/frigo/${day}`, payload, { headers: authHeaders() });
      setData((d) => ({ ...d, days: { ...(d?.days || {}), [day]: row } }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio'); }
    finally { setSavingDay(null); }
  };

  const cellCls = (esito) => {
    if (esito === 'C') return 'bg-emerald-50 text-emerald-900';
    if (esito === 'NC') return 'bg-red-100 text-red-900 font-bold';
    return 'bg-slate-50 text-slate-400 italic';
  };
  const esitoLabel = (e) => e === 'C' ? 'C' : e === 'NC' ? 'NC' : 'Da compilare';

  return (
    <div className="space-y-3" data-testid="frigo-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-lg font-bold text-[#023F80]">Registro temperature frigoriferi/congelatori</h3>
        <div className="flex gap-2 flex-wrap">
          <select value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))} data-testid="frg-month" className="px-2 py-1.5 border border-slate-300 rounded text-sm">
            {MESI.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} data-testid="frg-year" className="px-2 py-1.5 border border-slate-300 rounded text-sm">
            {[2024, 2025, 2026, 2027].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={dotId || ''} onChange={(e) => setDotId(e.target.value)} data-testid="frg-dotazione" className="px-2 py-1.5 border border-slate-300 rounded text-sm">
            {dotazioni.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
      </div>

      {dotazione && (
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 flex flex-wrap gap-x-6 gap-y-1">
          <div><b>Tipologia prodotto:</b> {dotazione.tipologiaLabel}</div>
          <div><b>Limite applicato:</b> <span className="font-mono">{dotazione.display}</span></div>
        </div>
      )}

      {loading && <Loader2 className="animate-spin text-slate-500" size={16} />}
      {!loading && dotazione && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs border border-slate-200">
            <thead className="bg-slate-100 text-slate-700">
              <tr>
                <th className="px-2 py-1.5 w-10 text-center">Giorno</th>
                <th className="px-2 py-1.5">Temp. 1 (°C)</th>
                <th className="px-2 py-1.5">Esito 1</th>
                <th className="px-2 py-1.5">Temp. 2 (°C)</th>
                <th className="px-2 py-1.5">Esito 2</th>
                <th className="px-2 py-1.5">Temp. 3 (°C)</th>
                <th className="px-2 py-1.5">Esito 3</th>
                <th className="px-2 py-1.5">Sigla</th>
                <th className="px-2 py-1.5">Igiene (manuale)</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: days }, (_, i) => i + 1).map((day) => {
                const row = data?.days?.[day] || {};
                const nc = row.giorno_nc;
                return (
                  <tr key={day} className={`border-t border-slate-200 ${nc ? 'bg-red-50' : ''}`} data-testid={`frg-row-${day}`}>
                    <td className="px-2 py-0.5 text-center font-bold text-slate-700">{day}{nc && <span className="ml-1 text-red-600 text-[9px]">NC</span>}</td>
                    {[1, 2, 3].map((idx) => (
                      <React.Fragment key={idx}>
                        <td className="px-1 py-0.5">
                          <input
                            type="text"
                            inputMode="decimal"
                            defaultValue={row[`t${idx}`] ?? ''}
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              const curr = row[`t${idx}`] ?? '';
                              if (String(curr) !== v) saveDay(day, { [`t${idx}`]: v === '' ? null : v });
                            }}
                            disabled={savingDay === day}
                            data-testid={`frg-t${idx}-${day}`}
                            className="w-16 px-1 py-0.5 border border-slate-200 rounded text-right"
                          />
                        </td>
                        <td className={`px-1 py-0.5 text-center text-[10px] ${cellCls(row[`esito_t${idx}`])}`}>
                          {esitoLabel(row[`esito_t${idx}`])}
                        </td>
                      </React.Fragment>
                    ))}
                    <td className="px-1 py-0.5">
                      <input
                        type="text"
                        defaultValue={row.sigla || ''}
                        onBlur={(e) => { if ((row.sigla || '') !== e.target.value) saveDay(day, { sigla: e.target.value }); }}
                        data-testid={`frg-sigla-${day}`}
                        className="w-12 px-1 py-0.5 border border-slate-200 rounded"
                      />
                    </td>
                    <td className="px-1 py-0.5">
                      <select
                        value={row.igiene || 'da_compilare'}
                        onChange={(e) => saveDay(day, { igiene: e.target.value })}
                        data-testid={`frg-igiene-${day}`}
                        className={`w-full px-1 py-0.5 border rounded text-[10px] ${cellCls(row.igiene === 'da_compilare' ? null : row.igiene)}`}
                      >
                        <option value="da_compilare">Da compilare</option>
                        <option value="C">C</option>
                        <option value="NC">NC</option>
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-700 space-y-1">
        <div><b>Condizioni:</b> Assenza di promiscuità e di merce non protetta, assenza di segni di sporco visibile, macchie, untuosità al tatto sulle parti a contatto e non con gli alimenti, assenza di odori sgradevoli o anomali, assenza di prodotti con caratteristiche organolettiche (odore, colore, consistenza) anomali.</div>
        <div><b>C:</b> Conforme ai limiti sopra indicati.</div>
        <div><b>NC:</b> Non conforme ai limiti sopra indicati.</div>
        <div className="text-slate-500 italic">La temperatura non implica la conformità delle condizioni igieniche: la colonna "Igiene" va compilata manualmente.</div>
      </div>
    </div>
  );
};

const ComingSoon = ({ label, details }) => (
  <div className="text-center py-10" data-testid="rh-coming-soon">
    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold mb-3">
      FASE 2 — In arrivo
    </div>
    <h3 className="text-lg font-bold text-slate-700">{label}</h3>
    <p className="text-sm text-slate-500 max-w-md mx-auto mt-2">{details}</p>
  </div>
);

// ─────────────── FORNITORI ───────────────
const FornitoriPanel = ({ cucinaId, isClient }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const empty = { ragioneSociale: '', prodotti: '', contattoNome: '', contattoTelefono: '', dataInvio: '', dataRicezione: '' };

  const reload = () => {
    setLoading(true);
    axios.get(`${API}/haccp-registri/cucine/${cucinaId}/fornitori`, { headers: authHeaders() })
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [cucinaId]);

  const save = async (data) => {
    try {
      if (editing?.id) {
        await axios.patch(`${API}/haccp-registri/fornitori/${editing.id}`, { ...data, cucinaId }, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/haccp-registri/fornitori`, { ...data, cucinaId }, { headers: authHeaders() });
      }
      toast.success('Fornitore salvato');
      setEditing(null);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const del = async (id) => {
    if (!window.confirm('Eliminare questo fornitore?')) return;
    try { await axios.delete(`${API}/haccp-registri/fornitori/${id}`, { headers: authHeaders() }); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="space-y-4" data-testid="fornitori-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-bold text-[#023F80]">Fornitori qualificati</h3>
          <p className="text-xs text-slate-500">Rev. corrente · Data scheda: <b>{today}</b></p>
        </div>
        <button onClick={() => setEditing(empty)} data-testid="forn-add-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#023F80] text-white text-xs font-bold hover:bg-[#022F60]">
          <Plus size={14} /> Nuovo fornitore
        </button>
      </div>
      {loading && <Loader2 className="animate-spin text-slate-500" size={16} />}
      {!loading && items.length === 0 && <div className="text-sm text-slate-500 italic">Nessun fornitore registrato.</div>}
      {!loading && items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs border border-slate-200 rounded-lg overflow-hidden">
            <thead className="bg-slate-50">
              <tr className="text-left">
                <th className="px-2 py-1.5 font-bold text-slate-600">Ragione sociale</th>
                <th className="px-2 py-1.5 font-bold text-slate-600">Prodotti</th>
                <th className="px-2 py-1.5 font-bold text-slate-600">Referente allerta</th>
                <th className="px-2 py-1.5 font-bold text-slate-600">Tel. allerta</th>
                <th className="px-2 py-1.5 font-bold text-slate-600">Invio richiesta</th>
                <th className="px-2 py-1.5 font-bold text-slate-600">Ricezione modulo</th>
                <th className="px-2 py-1.5 font-bold text-slate-600 w-16">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-2 py-1.5 font-semibold text-slate-800">{it.ragioneSociale}</td>
                  <td className="px-2 py-1.5">{it.prodotti || '—'}</td>
                  <td className="px-2 py-1.5">{it.contattoNome || '—'}</td>
                  <td className="px-2 py-1.5 font-mono">{it.contattoTelefono || '—'}</td>
                  <td className="px-2 py-1.5">{it.dataInvio || '—'}</td>
                  <td className="px-2 py-1.5">{it.dataRicezione || '—'}</td>
                  <td className="px-2 py-1.5 flex gap-1">
                    <button onClick={() => setEditing(it)} className="px-1.5 py-0.5 text-[10px] bg-slate-100 rounded hover:bg-slate-200">Mod.</button>
                    {!isClient && <button onClick={() => del(it.id)} className="px-1.5 py-0.5 text-[10px] bg-red-100 text-red-700 rounded hover:bg-red-200">Del</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <FornitoreDialog data={editing} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
};

const FornitoreDialog = ({ data, onSave, onClose }) => {
  const [form, setForm] = useState(data);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="forn-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-5 space-y-3">
        <h3 className="text-lg font-bold text-[#023F80]">{data.id ? 'Modifica fornitore' : 'Nuovo fornitore'}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Field label="Ragione sociale *"><Input value={form.ragioneSociale} onChange={(e) => upd('ragioneSociale', e.target.value)} data-testid="forn-ragione" /></Field>
          <Field label="Prodotti forniti"><Input value={form.prodotti} onChange={(e) => upd('prodotti', e.target.value)} /></Field>
          <Field label="Referente allerta"><Input value={form.contattoNome} onChange={(e) => upd('contattoNome', e.target.value)} /></Field>
          <Field label="Telefono allerta"><Input value={form.contattoTelefono} onChange={(e) => upd('contattoTelefono', e.target.value)} placeholder="Testuale (es. +39 ...)" /></Field>
          <Field label="Data invio richiesta"><Input type="date" value={form.dataInvio || ''} onChange={(e) => upd('dataInvio', e.target.value)} /></Field>
          <Field label="Data ricezione modulo"><Input type="date" value={form.dataRicezione || ''} onChange={(e) => upd('dataRicezione', e.target.value)} /></Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm hover:bg-slate-100">Annulla</button>
          <button onClick={() => onSave(form)} disabled={!form.ragioneSociale.trim()} data-testid="forn-save-btn" className="px-4 py-2 rounded-lg bg-[#023F80] text-white text-sm font-bold disabled:opacity-50">Salva</button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block text-xs font-bold text-slate-600">
    {label}
    <div className="mt-1">{children}</div>
  </label>
);

// ─────────────── INSETTI ───────────────
const InsettiPanel = ({ cucinaId, catalog, isClient }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear + 1, currentYear, currentYear - 1, currentYear - 2];

  const reload = () => {
    setLoading(true);
    axios.get(`${API}/haccp-registri/cucine/${cucinaId}/insetti`, { params: { year }, headers: authHeaders() })
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [cucinaId, year]);

  const delRilievo = async (id) => {
    if (!window.confirm('Eliminare definitivamente questo rilievo? L\'operazione viene tracciata nell\'audit log.')) return;
    try {
      await axios.delete(`${API}/haccp-registri/insetti/${id}`, { headers: authHeaders() });
      toast.success('Rilievo eliminato');
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const criticita = items.filter((i) => i.criticita).length;

  return (
    <div className="space-y-4" data-testid="insetti-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-bold text-[#023F80]">Check-list per rilievi ispettivi — insetti/roditori</h3>
          <p className="text-xs text-slate-500">Compila ad ogni controllo (anche in assenza di criticità).</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="ins-year" className="px-2 py-1.5 rounded-lg border-2 border-slate-200 text-xs font-bold text-slate-700 bg-white">
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={() => setFormOpen(true)} data-testid="ins-add-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#023F80] text-white text-xs font-bold hover:bg-[#022F60]">
            <Plus size={14} /> Nuovo rilievo
          </button>
        </div>
      </div>
      {!loading && items.length > 0 && (
        <div className="flex gap-2 text-[11px]">
          <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold">{items.length} rilievi {year}</span>
          {criticita > 0 && <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold">{criticita} con criticità</span>}
          {criticita === 0 && <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">0 criticità</span>}
        </div>
      )}
      {loading && <Loader2 className="animate-spin text-slate-500" size={16} />}
      {!loading && items.length === 0 && <div className="text-sm text-slate-500 italic">Nessun rilievo registrato per il {year}.</div>}
      {!loading && items.length > 0 && (
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id} className={`rounded-lg border p-3 ${it.criticita ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid={`ins-row-${it.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="font-bold text-slate-800">Controllo del {it.dataControllo}</div>
                  <div className="text-[11px] text-slate-500">
                    Compilato da {it.createdByName || it.createdBy} · {new Date(it.createdAt).toLocaleString('it-IT')}
                    {it.dataSchedaRevisione && <> · Rev. scheda: <b>{it.dataSchedaRevisione}</b></>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {it.criticita ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold"><AlertTriangle size={11} />Criticità</span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold">Nessuna criticità</span>
                  )}
                  {!isClient && (
                    <button onClick={() => delRilievo(it.id)} data-testid={`ins-del-${it.id}`} title="Elimina rilievo" className="p-1 rounded hover:bg-red-100 text-red-600">
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5 text-xs mt-2">
                {(catalog?.domandeInsetti || []).map((q) => {
                  const v = it.risposte?.[q.key];
                  return (
                    <div key={q.key} className="flex items-center gap-2">
                      <span className={`inline-block w-4 h-4 rounded text-[10px] text-white font-bold text-center ${v ? 'bg-red-600' : 'bg-slate-400'}`}>{v ? 'SI' : 'NO'}</span>
                      <span className="text-slate-700">{q.label}</span>
                    </div>
                  );
                })}
              </div>
              {it.azioniCorrettive && <div className="mt-2 text-xs bg-white rounded p-2 border border-slate-200"><b className="text-red-700">Azioni correttive:</b> {it.azioniCorrettive}</div>}
              <div className="flex gap-4 mt-2 text-[11px] text-slate-500">
                {it.firmaOperatore && <span>Sigla operatore: <b>{it.firmaOperatore}</b></span>}
                {it.verificaHaccp && <span>Verifica HACCP: <b>{it.verificaHaccp}</b></span>}
              </div>
            </div>
          ))}
        </div>
      )}
      {formOpen && <InsettiForm cucinaId={cucinaId} catalog={catalog} onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); reload(); }} />}
    </div>
  );
};

const InsettiForm = ({ cucinaId, catalog, onClose, onSaved }) => {
  const [data, setData] = useState(new Date().toISOString().slice(0, 10));
  const [dataRev, setDataRev] = useState('');
  const [risposte, setRisposte] = useState({});
  const [azioni, setAzioni] = useState('');
  const [firma, setFirma] = useState('');
  const [verifica, setVerifica] = useState('');
  const [saving, setSaving] = useState(false);
  const allAnswered = (catalog?.domandeInsetti || []).every((q) => q.key in risposte);
  const anyYes = Object.values(risposte).some(Boolean);
  const needsAzioni = anyYes && !azioni.trim();

  const submit = async () => {
    if (!allAnswered) return toast.error('Rispondi a tutte le domande (Sì/No)');
    if (needsAzioni) return toast.error('Hai segnalato criticità: compila le azioni correttive');
    setSaving(true);
    try {
      await axios.post(`${API}/haccp-registri/insetti`, { cucinaId, dataControllo: data, dataSchedaRevisione: dataRev, risposte, azioniCorrettive: azioni, firmaOperatore: firma, verificaHaccp: verifica }, { headers: authHeaders() });
      toast.success(anyYes ? 'Rilievo salvato con criticità segnalate' : 'Rilievo salvato — nessuna criticità');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); } finally { setSaving(false); }
  };

  const setAll = (val) => {
    const next = {};
    (catalog?.domandeInsetti || []).forEach((q) => { next[q.key] = val; });
    setRisposte(next);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ins-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-5 max-h-[90vh] overflow-y-auto space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#023F80]">Nuovo rilievo insetti/roditori</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Data del controllo *"><Input type="date" value={data} onChange={(e) => setData(e.target.value)} data-testid="ins-data" /></Field>
          <Field label="Rev. scheda (facolt.)"><Input type="date" value={dataRev} onChange={(e) => setDataRev(e.target.value)} data-testid="ins-rev" /></Field>
        </div>
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold text-slate-600">Rilievi (Sì = criticità)</div>
            <div className="flex gap-1 text-[10px]">
              <button type="button" onClick={() => setAll(false)} data-testid="ins-all-no" className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 font-bold border border-emerald-200 hover:bg-emerald-100">Tutti NO</button>
              <button type="button" onClick={() => setRisposte({})} data-testid="ins-reset" className="px-2 py-0.5 rounded bg-slate-50 text-slate-600 font-bold border border-slate-200 hover:bg-slate-100">Azzera</button>
            </div>
          </div>
          {(catalog?.domandeInsetti || []).map((q) => {
            const v = risposte[q.key];
            return (
              <div key={q.key} className="flex items-center justify-between py-1.5 border-b border-slate-100 text-xs">
                <span className="flex-1 text-slate-700 pr-2">{q.label}</span>
                <div className="flex gap-1 shrink-0">
                  <button type="button" onClick={() => setRisposte((r) => ({ ...r, [q.key]: true }))} data-testid={`ins-q-${q.key}-si`}
                    className={`px-2 py-0.5 rounded text-[11px] font-bold border-2 ${v === true ? 'bg-red-600 text-white border-red-700' : 'bg-white text-slate-500 border-slate-300'}`}>SI</button>
                  <button type="button" onClick={() => setRisposte((r) => ({ ...r, [q.key]: false }))} data-testid={`ins-q-${q.key}-no`}
                    className={`px-2 py-0.5 rounded text-[11px] font-bold border-2 ${v === false ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white text-slate-500 border-slate-300'}`}>NO</button>
                </div>
              </div>
            );
          })}
        </div>
        {anyYes && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-2 text-[11px] text-red-800">
            <AlertTriangle className="inline mr-1" size={12} /> Hai segnalato almeno una criticità: compila le azioni correttive.
          </div>
        )}
        <Field label={`Azioni correttive${anyYes ? ' *' : ''}`}>
          <textarea value={azioni} onChange={(e) => setAzioni(e.target.value)} rows={2} data-testid="ins-azioni" placeholder={anyYes ? 'Es: Chiamata ditta disinfestazione, posizionate trappole in area X, rimozione contenitori danneggiati…' : 'Facoltativo'} className={`w-full px-2 py-1.5 rounded border text-sm ${needsAzioni ? 'border-red-400 bg-red-50' : 'border-slate-300'}`} />
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Firma/Sigla operatore"><Input value={firma} onChange={(e) => setFirma(e.target.value)} data-testid="ins-firma" /></Field>
          <Field label="Verifica resp. HACCP"><Input value={verifica} onChange={(e) => setVerifica(e.target.value)} data-testid="ins-verifica" /></Field>
        </div>
        <div className="text-[10px] text-slate-400 italic">Le sigle/firme digitate NON costituiscono firma elettronica qualificata.</div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 rounded-lg text-sm hover:bg-slate-100">Annulla</button>
          <button onClick={submit} disabled={saving || !allAnswered || needsAzioni} data-testid="ins-save-btn" className="px-4 py-2 rounded-lg bg-[#023F80] text-white text-sm font-bold disabled:opacity-50">
            {saving ? 'Salvo…' : 'Salva rilievo'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────── CONFIG DIALOG (consulenti) ───────────────
const ConfigDialog = ({ companyId, catalog, cucine, onClose, onChanged }) => {
  const [tab, setTab] = useState('cucine');
  const [newCucina, setNewCucina] = useState({ name: '', sede: '', note: '' });
  const [selCucinaId, setSelCucinaId] = useState(cucine[0]?.id || null);
  const [dotazioni, setDotazioni] = useState([]);
  const [aree, setAree] = useState([]);
  const [config, setConfig] = useState([]);
  const [newDot, setNewDot] = useState({ kind: 'frigo', name: '', tipologiaKey: 'latte_pastorizzato', note: '' });
  const [newAreaName, setNewAreaName] = useState('');
  const [areaSuggerimenti, setAreaSuggerimenti] = useState([]);

  const reload = async () => {
    if (!selCucinaId) return;
    const [d, a, c, s] = await Promise.all([
      axios.get(`${API}/haccp-registri/cucine/${selCucinaId}/dotazioni?includeDisabled=true`, { headers: authHeaders() }),
      axios.get(`${API}/haccp-registri/cucine/${selCucinaId}/aree?includeDisabled=true`, { headers: authHeaders() }),
      axios.get(`${API}/haccp-registri/cucine/${selCucinaId}/pulizie/config`, { headers: authHeaders() }),
      axios.get(`${API}/haccp-registri/cucine/${selCucinaId}/aree/suggerimenti`, { headers: authHeaders() }).catch(() => ({ data: { items: [] } })),
    ]);
    setDotazioni(d.data); setAree(a.data); setConfig(c.data); setAreaSuggerimenti(s.data.items || []);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [selCucinaId]);

  const createCucina = async () => {
    if (!newCucina.name.trim()) return toast.error('Nome cucina obbligatorio');
    try {
      const { data } = await axios.post(`${API}/haccp-registri/cucine`, { ...newCucina, companyId }, { headers: authHeaders() });
      toast.success('Cucina creata');
      setNewCucina({ name: '', sede: '', note: '' });
      onChanged();
      setSelCucinaId(data.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const createDotazione = async () => {
    if (!newDot.name.trim()) return toast.error('Nome dotazione');
    try {
      await axios.post(`${API}/haccp-registri/dotazioni`, { ...newDot, cucinaId: selCucinaId }, { headers: authHeaders() });
      setNewDot({ kind: 'frigo', name: '', tipologiaKey: 'latte_pastorizzato', note: '' });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const toggleDot = async (id) => { await axios.post(`${API}/haccp-registri/dotazioni/${id}/toggle`, {}, { headers: authHeaders() }); reload(); };
  const createArea = async (name) => {
    const n = (name || newAreaName).trim();
    if (!n) return;
    try {
      await axios.post(`${API}/haccp-registri/aree`, { cucinaId: selCucinaId, name: n }, { headers: authHeaders() });
      setNewAreaName('');
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const toggleArea = async (id) => { await axios.post(`${API}/haccp-registri/aree/${id}/toggle`, {}, { headers: authHeaders() }); reload(); };
  const togglePulConfig = async (scheda, enabled) => {
    try {
      await axios.post(`${API}/haccp-registri/pulizie/config`, { cucinaId: selCucinaId, scheda, enabled }, { headers: authHeaders() });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="rh-config-dialog">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl p-5 max-h-[92vh] overflow-y-auto space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#023F80]">Configurazione cucine · {cucine.length} cucine</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X size={18} /></button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 flex-wrap">
          {[['cucine', 'Cucine'], ['dotazioni', 'Dotazioni'], ['aree', 'Aree'], ['pulizia', 'Schede pulizia']].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`cfg-tab-${k}`} className={`px-3 py-1.5 rounded-lg text-xs font-bold border-2 ${tab === k ? 'bg-[#023F80] text-white border-[#023F80]' : 'bg-white text-slate-600 border-slate-200'}`}>{l}</button>
          ))}
        </div>

        {tab === 'cucine' && (
          <div className="space-y-3">
            <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
              <div className="text-[11px] font-bold uppercase text-slate-600 mb-2">Crea nuova cucina</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Input placeholder="Nome cucina" value={newCucina.name} onChange={(e) => setNewCucina((c) => ({ ...c, name: e.target.value }))} data-testid="cfg-cuc-name" />
                <Input placeholder="Sede (opz.)" value={newCucina.sede} onChange={(e) => setNewCucina((c) => ({ ...c, sede: e.target.value }))} />
                <button onClick={createCucina} data-testid="cfg-cuc-create-btn" className="px-3 py-2 rounded-lg bg-[#023F80] text-white text-sm font-bold">+ Crea cucina</button>
              </div>
            </div>
            <div className="space-y-1">
              {cucine.map((c) => (
                <div key={c.id} className={`flex items-center gap-2 p-2 rounded border ${selCucinaId === c.id ? 'border-[#023F80] bg-blue-50' : 'border-slate-200'}`}>
                  <input type="radio" name="selcuc" checked={selCucinaId === c.id} onChange={() => setSelCucinaId(c.id)} />
                  <span className="flex-1 text-sm font-semibold">{c.name}</span>
                  <span className="text-[10px] text-slate-500">{c.sede}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'dotazioni' && (
          <div className="space-y-3">
            <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
              <div className="text-[11px] font-bold uppercase text-slate-600 mb-2">Aggiungi dotazione</div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <select value={newDot.kind} onChange={(e) => setNewDot((d) => ({ ...d, kind: e.target.value }))} className="px-2 py-1.5 rounded border border-slate-300 text-sm" data-testid="cfg-dot-kind">
                  <option value="frigo">Frigorifero</option>
                  <option value="congelatore">Congelatore</option>
                </select>
                <Input placeholder="Es. Congelatore 1 — Surgelati" value={newDot.name} onChange={(e) => setNewDot((d) => ({ ...d, name: e.target.value }))} data-testid="cfg-dot-name" />
                <select value={newDot.tipologiaKey} onChange={(e) => setNewDot((d) => ({ ...d, tipologiaKey: e.target.value }))} className="px-2 py-1.5 rounded border border-slate-300 text-sm" data-testid="cfg-dot-tipologia">
                  {(catalog?.tipologieFrigo || []).map((t) => (
                    <option key={t.key} value={t.key}>{t.label.length > 50 ? t.label.slice(0, 50) + '…' : t.label} ({t.display})</option>
                  ))}
                </select>
                <button onClick={createDotazione} data-testid="cfg-dot-create-btn" className="px-3 py-2 rounded-lg bg-[#023F80] text-white text-sm font-bold">+ Aggiungi</button>
              </div>
            </div>
            <div className="space-y-1">
              {dotazioni.map((d) => (
                <div key={d.id} className={`flex items-center gap-2 p-2 rounded border text-xs ${d.disabled ? 'bg-slate-100 opacity-60 border-slate-200' : 'bg-white border-slate-200'}`}>
                  <span className="shrink-0 px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-bold">{d.kind}</span>
                  <span className="flex-1 font-semibold text-slate-800">{d.name}</span>
                  <span className="text-slate-500">{d.display}</span>
                  <button onClick={() => toggleDot(d.id)} data-testid={`cfg-dot-toggle-${d.id}`} className={`px-2 py-0.5 rounded text-[10px] font-bold ${d.disabled ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {d.disabled ? 'Riattiva' : 'Disattiva'}
                  </button>
                </div>
              ))}
              {dotazioni.length === 0 && <div className="text-sm text-slate-500 italic">Nessuna dotazione.</div>}
            </div>
          </div>
        )}

        {tab === 'aree' && (
          <div className="space-y-3">
            <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
              <div className="text-[11px] font-bold uppercase text-slate-600 mb-2">Aggiungi area</div>
              <div className="flex gap-2">
                <Input placeholder="Nome area (es. Area cucina)" value={newAreaName} onChange={(e) => setNewAreaName(e.target.value)} data-testid="cfg-area-name" />
                <button onClick={() => createArea(null)} data-testid="cfg-area-create-btn" className="px-3 py-2 rounded-lg bg-[#023F80] text-white text-sm font-bold shrink-0">+ Aggiungi</button>
              </div>
              {areaSuggerimenti.length > 0 && (
                <div className="mt-2">
                  <div className="text-[10px] font-bold uppercase text-slate-500 mb-1">Riutilizza da altre cucine della stessa azienda:</div>
                  <div className="flex flex-wrap gap-1">
                    {areaSuggerimenti.filter((n) => !aree.some((a) => a.name.toLowerCase() === n.toLowerCase())).map((n) => (
                      <button key={n} onClick={() => createArea(n)} className="px-2 py-0.5 rounded text-[11px] bg-blue-100 text-blue-800 hover:bg-blue-200">+ {n}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-1">
              {aree.map((a) => (
                <div key={a.id} className={`flex items-center gap-2 p-2 rounded border text-xs ${a.disabled ? 'bg-slate-100 opacity-60 border-slate-200' : 'bg-white border-slate-200'}`}>
                  <span className="flex-1 font-semibold text-slate-800">{a.name}</span>
                  <button onClick={() => toggleArea(a.id)} className={`px-2 py-0.5 rounded text-[10px] font-bold ${a.disabled ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {a.disabled ? 'Riattiva' : 'Disattiva'}
                  </button>
                </div>
              ))}
              {aree.length === 0 && <div className="text-sm text-slate-500 italic">Nessuna area configurata.</div>}
            </div>
          </div>
        )}

        {tab === 'pulizia' && (
          <div className="space-y-2">
            <div className="text-xs text-slate-500">Scegli quali schede del catalogo attivare per questa cucina. Il cliente vedrà solo quelle attive.</div>
            {(catalog?.schedePulizia || []).map((s) => {
              const row = config.find((c) => c.scheda === s.code);
              const enabled = row?.enabled || false;
              return (
                <div key={s.code} className={`rounded border p-2 ${enabled ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`}>
                  <div className="flex items-center gap-2">
                    <Checkbox checked={enabled} onCheckedChange={(v) => togglePulConfig(s.code, !!v)} data-testid={`cfg-pul-${s.code}`} />
                    <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 text-[10px] font-bold">{s.code}</span>
                    <span className="flex-1 font-semibold text-sm">{s.label}</span>
                    <span className="text-[10px] text-slate-500">{s.frequency}</span>
                  </div>
                  {enabled && (
                    <div className="mt-1 pl-6 text-[11px] text-slate-600">
                      Voci: {(row?.items || s.items).join(' · ')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-end pt-2 border-t">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm font-bold">Chiudi</button>
        </div>
      </div>
    </div>
  );
};

export default RegistriHaccpPage;
